import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type {
  UpcomingSession,
  UpcomingSessionsResponse,
  SessionErrorResponse,
} from '../types';

export const getUpcomingSessions = (): Promise<UpcomingSessionsResponse> => {
  return axios.get('/user/v2/upcoming-session');
};

function* generateUpcomingSessions(
  count: number = 2,
  baseTimes?: number[]
): Generator<UpcomingSession> {
  for (let i = 0; i < count; i++) {
    const baseTime = baseTimes?.[i] ?? Date.now() + (i + 1) * 24 * 3_600_000; // Use provided base time or default
    const sessionDate = new Date(baseTime);

    // Round to the nearest hour (set minutes, seconds, milliseconds to 0)
    sessionDate.setMinutes(0, 0, 0);

    yield {
      session_type: faker.helpers.arrayElement([
        'Batch Session',
        'Demo Session',
      ]),
      session_id: faker.string.uuid(),
      upcoming: true,
      number: i + 1,
      batch_allocated: faker.datatype.boolean(),
      timestamp: sessionDate.getTime(),
      timezone: 'Asia/Kolkata',
      video: faker.internet.url(),
      delivery_type: faker.helpers.arrayElement(['offline']),
      input_stream: faker.internet.url(),
      input_stream_duration: faker.number.int({ min: 30, max: 120 }).toString(),
      assessment_room_id: faker.string.alphanumeric(10),
      platform: faker.helpers.arrayElement(['100ms', 'ivs']),
      link: faker.internet.url(),
      room_id: faker.string.alphanumeric(8),
      readable_date: sessionDate.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
      }),
      readable_time: sessionDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      readable_day: sessionDate.toLocaleDateString('en-US', {
        weekday: 'long',
      }),
      // remaining_seconds: 900,
      remaining_seconds: 90_000,
      // remaining_seconds: Math.floor(
      //   (sessionDate.getTime() - Date.now()) / 1000
      // ),
      payment_link: faker.datatype.boolean() ? faker.internet.url() : null,
      trial: faker.datatype.boolean() ? faker.string.alphanumeric(6) : null,
      allow_session_join: faker.datatype.boolean(),
      subject: faker.helpers.arrayElement(['Math', 'Chess', 'English']),
      variant_id: faker.string.uuid(),
    };
  }
}

export const upcomingSessionMockHandler = http.get(
  `${env.API_URL}/user/v2/upcoming-session`,
  async (): Promise<
    HttpResponse<UpcomingSessionsResponse | SessionErrorResponse>
  > => {
    await networkDelay();

    // Always return sessions for predictable behavior in tests and development
    const hasUpcomingSession = true;

    if (!hasUpcomingSession) {
      return HttpResponse.json(
        {
          status: 'FAILURE',
          message: 'No upcoming session',
          exception: 'application-exception',
        },
        { status: 200 }
      );
    }

    // Randomize number of upcoming sessions (1 to 2)
    const sessionCount = faker.number.int({ min: 2, max: 2 });

    // Use predictable base times for consistent behavior
    const now = Date.now();
    const baseTimes: number[] = [];
    // Use predictable times: 30 minutes and 90 minutes from now
    baseTimes.push(
      now + 1800 * 1000, // 30 minutes
      now + (1800 + 3600) * 1000 // 90 minutes
    );

    const randomizedSessions = [
      ...generateUpcomingSessions(sessionCount, baseTimes),
    ];

    const response: UpcomingSessionsResponse = {
      sessions: randomizedSessions,
      status: 'success',
      message: `Found ${sessionCount} upcoming session${sessionCount > 1 ? 's' : ''}`,
      batch_allocated: faker.datatype.boolean(),
      upcoming: true,
    };

    return HttpResponse.json(response);
  }
);
